import { ModulesStates, type TagCategoriesConfig } from '@racemap/sdk/schema/events';
import type { PlayerEventInfo, PlayerStarter } from '@racemap/sdk/schema/player';
import { DEFAULT_GEO_LINESTRING_LINE_WIDTH } from '@racemap/utilities/consts/geoLineString';
import {
  OneHourInMillis,
  OneMinuteInMillis,
  OneSecondInMillis,
} from '@racemap/utilities/consts/time';
import { getSortedTagCategoriesConfigEntries } from '@racemap/utilities/functions/starterTags/getSortedTagCategoriesConfigEntries';
import type { UnitType } from '@racemap/utilities/types/units';
import type { Immutable } from 'immer';
import { DEFAULT_TILE_SIZE, SHADOW_TRACK_STEP } from './constants';
import type { ExternalLayerFeature, LocatedPoiFeature, PoiFeature, TrackFeature } from './geojson';
import { PlayerColor } from './PlayerColor';
import {
  type ChunkInfo,
  type EventChangeableState,
  type EventState,
  type ExternalLayerType,
  FlagContent,
  InfoSheetTabs,
  type MapType,
  type NetReplayModule,
  PinToShadowTrackMode,
  type PoiType,
  type StarterType,
  TimeMode,
  type TrackType,
  toPlayerFlagContent,
} from './store';
import { correctedNow } from './utils/clock';
import type Option from './utils/option';
import TrackView from './utils/TrackView';

/**
 * The wire payload of `GET /api/player/:eventId/info`, straight from the
 * schema the backend validates its response against.
 */
export type RawEvent = PlayerEventInfo;
type RawStarter = PlayerStarter;
export type RawEventOptions = PlayerEventInfo['playerOptions'];
type RawNetReplay = PlayerEventInfo['modules']['netReplay'];

export type EventStateWithOptions = EventState & {
  globalOptions: {
    mapType: MapType;
    unitType: UnitType;
    timeMode: TimeMode;
    showAllFlags: boolean;
    pinToShadowTrack: PinToShadowTrackMode;
    tagCategoriesConfig: TagCategoriesConfig;
    largeMode: boolean;
  };
  changeable: EventChangeableState;
};

export function getVisibleStarterGroups(
  tagCategoriesConfig: Immutable<RawEventOptions['tagCategoriesConfig']>,
): Array<string> {
  return getSortedTagCategoriesConfigEntries(tagCategoriesConfig)
    .filter(([, config]) => config.visible && config.groupStarterBy)
    .map(([category]) => category);
}

export type EventStateWithOptionsAndNulledChangeable = Omit<EventStateWithOptions, 'changeable'> & {
  changeable: null;
};

function parseStarter(
  rawStarter: RawStarter,
  index: number,
  eventIndex: number,
  tagCategoriesConfig: RawEventOptions['tagCategoriesConfig'] | null = null,
  baseColor: string | null = null,
): StarterType {
  const color = PlayerColor.from(rawStarter.markerColor ?? baseColor);
  return {
    type: 'STARTER',
    index,
    id: rawStarter.id,
    eventIndex,
    name: rawStarter.name || undefined,
    startNumber: rawStarter.startNumber || undefined,
    searchText: `${rawStarter.name} ${rawStarter.startNumber}`,
    deviceClass: rawStarter.deviceClass,
    markerColor: color,
    tags:
      rawStarter.tags != null
        ? new Map(
            Object.entries(rawStarter.tags)
              // Unknown/new categories are visible by default unless explicitly configured as hidden.
              .filter(([k]) => tagCategoriesConfig?.[k]?.visible ?? true)
              .map(([k, v]) => [k, `${v}`.trim()] as [string, string])
              .filter(([, v]) => v !== ''),
          )
        : new Map(),
  };
}

function parsePoi(
  rawPoi: LocatedPoiFeature,
  index: number,
  eventIndex: number,
  baseColor: string | null = null,
): PoiType {
  const colorObj = PlayerColor.from(rawPoi.properties.color ?? baseColor);

  const name = (rawPoi.properties.name ?? rawPoi.properties.title ?? '').trim();
  let shortName = rawPoi.properties.shortName?.trim();
  if (shortName === '') {
    shortName = name.substring(0, 6);
  }
  return {
    type: 'POI',
    index,
    id: `${rawPoi.properties.id ?? rawPoi.id}_${eventIndex}`,
    eventIndex,
    name,
    shortName,
    icon: rawPoi.properties.icon ?? undefined,
    latlng: { lng: rawPoi.geometry.coordinates[0], lat: rawPoi.geometry.coordinates[1] },
    offset: rawPoi.properties.offset ?? undefined,
    markerColor: colorObj,
  };
}

function parseTrack(
  rawTrack: TrackFeature,
  index: number,
  eventIndex: number,
  baseColor: string | null = null,
): TrackType {
  // TODO: replace input color array with rgba string or another more flexible format in the backend to avoid this complicated parsing logic
  const colorObj = PlayerColor.from(rawTrack.properties.color ?? baseColor);
  return {
    type: 'TRACK',
    index,
    id: `${rawTrack.properties.id ?? rawTrack.id}_${eventIndex}`,
    eventIndex,
    name: rawTrack.properties.name ?? '',
    coordinates: rawTrack.geometry.coordinates,
    markerColor: colorObj,
    lineWidth: rawTrack.properties.lineWidth ?? DEFAULT_GEO_LINESTRING_LINE_WIDTH,
  };
}

function parseExternalLayer(
  rawExternalLayer: ExternalLayerFeature,
  index: number,
  eventIndex: number,
): ExternalLayerType {
  return {
    type: 'EXTERNAL_LAYER',
    index,
    id: `${rawExternalLayer.id}_${eventIndex}`,
    eventIndex,
    layerType: rawExternalLayer.properties.layerType,
    tiles: rawExternalLayer.properties.tiles,
    tileSize: rawExternalLayer.properties.tileSize ?? DEFAULT_TILE_SIZE,
  };
}

export function parseDate(input: string | number): number {
  const regexp =
    /(\d\d\d\d)(-)?(\d\d)(-)?(\d\d)([T ])?(\d\d)(:)?(\d\d)(:)?(\d\d)(\.\d+)?(Z|([+-])(\d\d)(:)?(\d\d))/;

  const inputString = input.toString();
  const d = inputString.match(regexp);
  if (d != null) {
    let offset = 0;

    const date = new Date(
      Date.UTC(
        Number.parseInt(d[1], 10),
        Number.parseInt(d[3], 10) - 1,
        Number.parseInt(d[5], 10),
        Number.parseInt(d[7], 10),
        Number.parseInt(d[9], 10),
        Number.parseInt(d[11], 10),
      ),
    );
    if (d[12]) {
      date.setUTCMilliseconds(Number.parseFloat(d[12]) * OneSecondInMillis);
    } else {
      date.setUTCMilliseconds(0);
    }

    if (d[13] !== 'Z') {
      offset = Number.parseInt(d[15], 10) * 60 + Number.parseInt(d[17], 10);
      offset *= d[14] === '-' ? -1 : 1;
      date.setTime(date.getTime() - offset * OneMinuteInMillis);
    }
    return date.getTime();
  }
  return new Date(input).getTime();
}

export function parseChunks(buf: ArrayBuffer): Array<ChunkInfo> {
  const output = [];
  const view = new DataView(buf);
  let ptr = 0;
  while (ptr < buf.byteLength) {
    const chunkOffset = ptr + 8 + 8 + 4 + 4;
    const chunkStartTime = view.getFloat64(ptr, true);
    const chunkEndTime = view.getFloat64(ptr + 8, true);
    // encoded chunk length belongs to compressed chunk
    // so we have to subtract 4 bytes for the length itself
    const chunkLength = view.getUint32(ptr + 8 + 8, true) - 4;
    const encodedLength = view.getUint32(ptr + 8 + 8 + 4, true);
    const chunkInfo = {
      id: `${chunkStartTime}--${chunkEndTime}`,
      compressedBuffer: buf.slice(chunkOffset, chunkOffset + chunkLength),
      startTime: chunkStartTime,
      endTime: chunkEndTime,
      encodedLength,
      decodedLength: chunkLength,
    };
    output.push(chunkInfo);
    ptr += chunkLength + 8 + 8 + 4 + 4;
  }
  return output;
}

function parseNetReplay(
  rawNetReplay: RawNetReplay,
  rawEvent: RawEvent,
  shadowTrackLength: number | null,
): NetReplayModule {
  if (
    rawEvent.startTime == null ||
    rawNetReplay == null ||
    !rawNetReplay.enabled ||
    rawNetReplay.splitId == null ||
    rawNetReplay.splitId === ''
  )
    return { enabled: false };

  // Splits are geoJSON features; the ones the player does not draw are sent
  // without a geometry but are still found here.
  const split = rawEvent.geoJSON.features.find(
    (feature) =>
      feature.properties.racemapType === 'split' && feature.properties.id === rawNetReplay.splitId,
  );
  if (split == null) return { enabled: false };

  const syncOffset = (split as PoiFeature).properties.offset || 0;
  const duration = rawNetReplay.sliderDuration;
  const syncTime =
    Date.parse(rawEvent.startTime) + duration * (syncOffset / (shadowTrackLength ?? Number.NaN));

  return {
    enabled: true,
    syncOffset,
    syncTime: new Date(syncTime),
    sliderStartTime: new Date(rawEvent.startTime),
    sliderEndTime: new Date(Date.parse(rawEvent.startTime) + duration),
  };
}

function parseShadowTrackLength(rawEvent: RawEvent, shadowTrack: TrackView | null): number | null {
  if (shadowTrack == null) return null;
  return rawEvent.geo.shadowTrackLength ?? shadowTrack.size() * SHADOW_TRACK_STEP;
}

export function parseEvent(
  eventIndex: number,
  rawEvent: RawEvent,
  chunkedShadowTrack: Option<ArrayBufferLike>,
): EventStateWithOptionsAndNulledChangeable {
  const { startTime, endTime, isRepeating, startersCount } = rawEvent;
  const shadowTrack = chunkedShadowTrack.map(parseChunkedShadowTrack).getNullable();
  const shadowTrackLength = parseShadowTrackLength(rawEvent, shadowTrack);
  const netReplay = parseNetReplay(rawEvent.modules.netReplay, rawEvent, shadowTrackLength);

  let _startTime = correctedNow();
  let _endTime = _startTime + OneHourInMillis;
  if (startTime != null) {
    _startTime = parseDate(startTime);
  }
  if (endTime != null) {
    _endTime = parseDate(endTime);
  }
  if (netReplay.enabled && netReplay.sliderEndTime != null) {
    _endTime = netReplay.sliderEndTime.getTime();
  }

  if (isRepeating) {
    const eventDuration = _endTime - _startTime;
    const repeatTimeOffset =
      Math.floor((correctedNow() - _startTime) / eventDuration) * eventDuration;
    _startTime = _startTime + repeatTimeOffset;
    _endTime = _endTime + repeatTimeOffset;
  }

  return {
    eventId: rawEvent.id,
    index: eventIndex,
    name: rawEvent.name || '',
    shortName: rawEvent.name || '',
    slug: rawEvent.slug || rawEvent.id,
    startTime: _startTime,
    endTime: _endTime,
    eventDescription: rawEvent.eventDescription,
    eventWebsite: rawEvent.eventWebsite,
    logoUrl: rawEvent.logoUrl,
    eventContactMail: rawEvent.eventContactMail,
    eventLocation: rawEvent.location,
    updatedAt: new Date(rawEvent.updatedAt),
    shadowgeojsonHash: rawEvent.geo.shadowgeojsonHash ?? null,
    starters: rawEvent.starters.map((starter, index) =>
      parseStarter(
        starter,
        index,
        eventIndex,
        rawEvent.playerOptions.tagCategoriesConfig,
        rawEvent.playerOptions.baseColor,
      ),
    ),
    startersCount,
    pois: rawEvent.geoJSON.features
      .filter((f): f is LocatedPoiFeature => f.geometry?.type === 'Point')
      .map((f, index) => parsePoi(f, index, eventIndex, rawEvent.playerOptions.baseColor)),
    // Hidden tracks and tile-less external layers never arrive — the backend
    // drops them, see isFeatureVisibleInPlayer.
    tracks: rawEvent.geoJSON.features
      .filter((f): f is TrackFeature => f.properties.racemapType === 'track')
      .map((f, index) => parseTrack(f, index, eventIndex, rawEvent.playerOptions.baseColor)),
    externalLayers: rawEvent.geoJSON.features
      .filter((f): f is ExternalLayerFeature => f.properties.racemapType === 'externalLayer')
      .map((f, index) => parseExternalLayer(f, index, eventIndex)),
    options: {
      stepWidth: rawEvent.playerOptions.stepWidth,
      replaySpeedup: rawEvent.playerOptions.replaySpeedup,
      markerTimeout: rawEvent.playerOptions.markerTimeout,
      pollingInterval: rawEvent.playerOptions.pollingInterval,
      liveDelay: rawEvent.playerOptions.liveDelay,
      tailTimeout: rawEvent.playerOptions.tailTimeout ?? OneHourInMillis,
      elevationChartOptions: rawEvent.playerOptions.elevationChartOptions,
      viewport: rawEvent.playerOptions.viewport,
      flagContent: toPlayerFlagContent(rawEvent.playerOptions.flagContent),
      selectedFlagContent: FlagContent.STARTNUMBER_AND_NAME,
      tagCategoriesConfig: rawEvent.playerOptions.tagCategoriesConfig,
      listOpenByDefault: rawEvent.playerOptions.listOpenByDefault ?? true,
      infoTabs: {
        openByDefault: rawEvent.playerOptions.infoTabs?.openByDefault ?? true,
        defaultTab:
          (rawEvent.playerOptions.infoTabs?.defaultTab as InfoSheetTabs) ?? InfoSheetTabs.ELEVATION,
        disabledTabs: (rawEvent.playerOptions.infoTabs?.disabledTabs ?? []) as Array<InfoSheetTabs>,
      },
      showDistanceMarkers: rawEvent.playerOptions.showDistanceMarkers,
      reloadInterval: rawEvent.playerOptions.reloadInterval ?? 0,
      hasRaceVideo: rawEvent.playerOptions.hasRaceVideo,
    },
    modules: {
      timing:
        rawEvent.modules.timing.state !== ModulesStates.NONE
          ? {
              enabled: true,
              leaderboardUrl:
                rawEvent.modules.timing.leaderboard.type === 'EXTERNAL'
                  ? rawEvent.modules.timing.leaderboard.externalLeaderboardUrl
                  : `/leaderboard/${rawEvent.slug}?embed=true`,
            }
          : { enabled: false },

      sponsor:
        rawEvent.modules.sponsor.state !== ModulesStates.ACTIVATED
          ? { enabled: false }
          : {
              enabled: true,
              alignment: rawEvent.modules.sponsor.alignment,
              logos: rawEvent.modules.sponsor.logos,
            },
      netReplay,
      projection: rawEvent.modules.projection,
    },
    globalOptions: {
      mapType: rawEvent.playerOptions.mapType,
      unitType: rawEvent.playerOptions.unitType,
      timeMode: rawEvent.playerOptions.timeMode ?? TimeMode.RELATIVE,
      showAllFlags: rawEvent.playerOptions.showAllFlags,
      pinToShadowTrack: rawEvent.modules.projection.enabled
        ? PinToShadowTrackMode.FORCED
        : rawEvent.playerOptions.pinToShadowTrack,
      tagCategoriesConfig: rawEvent.playerOptions.tagCategoriesConfig,
      largeMode: rawEvent.playerOptions.largeMode ?? false,
    },
    changeable: null,
    shadowTrackIsVisible: shadowTrack != null && rawEvent.shadowTrackIsVisible,
    shadowTrackId: rawEvent.geo.shadowTrackId,
    chunkedShadowTrack: shadowTrack,
    shadowTrackLength,
  };
}

export function parseChunkedShadowTrack(buffer: ArrayBufferLike): TrackView {
  return new TrackView(new Float64Array(buffer));
}
